/**
 * Pure CSV builder behind the employee directory's Export action (#23608
 * follow-up: the button rendered with no `onClick` at all, so pressing it
 * did nothing on web and in the Capacitor shell alike).
 *
 * React- and DOM-free on purpose (CLAUDE.md § "Component Testability"): the
 * screen owns the JSX and the `triggerDownload` side effect, this file owns
 * the bytes, so the column set and the escaping are unit-testable without a
 * renderer.
 *
 * The export mirrors exactly the columns the directory table renders — never
 * the wider `Employee` shape. `basic_salary`, `bank` and `tin` are
 * deliberately excluded: putting compensation and bank details behind a
 * button every directory viewer can press is a permissions decision, not a
 * formatting one, and no such gate exists on this screen today.
 */
import type {
  Employee,
  EmploymentStatus,
  EmploymentType,
} from '../../data/types';

/**
 * RFC 4180 record separator. Excel on Windows is the realistic destination
 * for a payroll employee list, and it is the one consumer that still treats
 * a bare `\n` as a quirk rather than a line break in some import paths.
 */
const ROW_SEPARATOR = '\r\n';

/**
 * UTF-8 byte-order mark. Without it Excel decodes the file as the system
 * ANSI code page and renders every Sinhala and Tamil department name as
 * mojibake — the directory is localised, so the export has to be too.
 */
export const UTF8_BOM = '\uFEFF';

export const EMPLOYEE_DIRECTORY_CSV_MIME = 'text/csv;charset=utf-8;';

/**
 * Escape one CSV field. Always quoted (never conditionally), so a comma,
 * quote or newline inside tenant-typed free text — a department called
 * `Sales, EMEA`, a name with an apostrophe — can never shift the remaining
 * columns of that row.
 */
function csv_field(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/** Column headers, already translated by the caller. */
export interface EmployeeDirectoryCsvLabels {
  employee: string;
  department: string;
  role: string;
  type: string;
  status: string;
}

/**
 * Locale-bound lookups the screen supplies. Keeping them as injected
 * functions is what lets this module stay free of `useTranslation`.
 */
export interface EmployeeDirectoryCsvResolvers {
  /** Tenant free text (department / job title) rendered in the active locale. */
  localise: (value: string) => string;
  employment_type: (type: EmploymentType) => string;
  employment_status: (status: EmploymentStatus) => string;
}

/**
 * Build the CSV body for the rows currently on screen — i.e. after search
 * filtering and in the active sort direction, so what downloads is what the
 * user is looking at, not the unfiltered directory.
 */
export function build_employee_directory_csv(
  rows: readonly Employee[],
  labels: EmployeeDirectoryCsvLabels,
  resolve: EmployeeDirectoryCsvResolvers,
): string {
  const lines = [
    [
      labels.employee,
      labels.department,
      labels.role,
      labels.type,
      labels.status,
    ]
      .map(csv_field)
      .join(','),
  ];

  for (const employee of rows) {
    lines.push(
      [
        csv_field(employee.name),
        csv_field(resolve.localise(employee.department)),
        csv_field(resolve.localise(employee.job_title)),
        csv_field(resolve.employment_type(employee.employment_type)),
        csv_field(resolve.employment_status(employee.status)),
      ].join(','),
    );
  }

  return lines.join(ROW_SEPARATOR);
}

/**
 * `employee-directory-2026-08-26.csv`. Takes the date rather than reading
 * the clock so the name is deterministic under test; local calendar parts
 * (not `toISOString()`) so a Colombo evening export is not stamped with
 * tomorrow's UTC date.
 */
export function employee_directory_csv_filename(today: Date): string {
  const year = String(today.getFullYear()).padStart(4, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `employee-directory-${year}-${month}-${day}.csv`;
}
